/*
  extras-gallery-rotate.js — auto-rotation for the bottom-region PDP
  galleries (Slab No Hardware / Closet / Corner / Wall Hook Racks).

  Strategy
  --------
  The galleries themselves are Focal's <product-gallery> + <media-carousel>
  + <page-dots> components, which already handle scroll-snap, click-thumb-
  to-scroll, drag, keyboard navigation, and the multi-image zoom modal.
  Auto-rotation is the one thing those components don't do by default —
  this script adds it on top by scrolling the carousel, the same way the
  customer would by swiping. <page-dots> picks up the new scroll position
  and updates the active thumbnail automatically; no need to touch its
  state.

  Performance + battery
  ---------------------
  - IntersectionObserver gates EVERYTHING: rotation timers don't start, and
    slide 2's image isn't preloaded, until the section is within 200px of
    the viewport. Sections the customer never scrolls to cost nothing.
  - Pauses on mouseenter / touchstart / focusin so the customer can
    inspect a slide without it moving under them.
  - Permanently pauses for the page lifetime after a user click on a
    thumbnail or a manual drag — once the customer takes control, the
    script stops competing.
  - Stops on the first prefers-reduced-motion query and on saveData
    (navigator.connection.saveData) — both signal "don't burn bandwidth".
  - Pauses while the tab is hidden (document.visibilityState).
  - Caps total rotation time at 30 s after first becoming visible —
    customer either engaged or wasn't interested; saves battery on
    long-dwell pages.

  Markup contract
  ---------------
  Each rotation-enabled carousel has `data-extras-rotate` on the
  <media-carousel> element. The script finds them at DOMContentLoaded
  AND on dynamic-insertion via a MutationObserver, so it handles Shopify
  section-renders (Focal re-renders the product block on variant change).

  Also bundles a small `[data-xgal-zoom][data-xgal-src]` click delegate so
  the hook-board configurator's per-size-card eye-zoom buttons keep
  working — those open a single explicit image in the shared .cfg-modal,
  which is distinct from the section gallery's multi-image zoom.
*/

'use strict';

(function () {
  const ROTATE_MS         = 4500;           // Billy's "4-5 second interval"
  const ROTATE_CAP_MS     = 30000;          // stop auto-rotation after 30s in view
  const OBSERVER_MARGIN   = '200px 0px';    // start prep work just before in view
  const SLOT             = '[data-extras-rotate]';

  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData     = !!(navigator.connection && navigator.connection.saveData);

  // Single shared observer — one observer per page, not per gallery.
  // Scales cleanly to N sections without piling up observers.
  let observer = null;
  // Pre-allocated to avoid per-gallery closure overhead.
  const galleries = new Map(); // <media-carousel> → state

  function ensureObserver() {
    if (observer) return observer;
    observer = new IntersectionObserver(onIntersect, {
      rootMargin: OBSERVER_MARGIN,
      threshold:  0,
    });
    return observer;
  }

  function onIntersect(entries) {
    entries.forEach(entry => {
      const state = galleries.get(entry.target);
      if (!state) return;
      if (entry.isIntersecting) onEnterViewport(state);
      else                       onLeaveViewport(state);
    });
  }

  function onEnterViewport(state) {
    if (state.firstSeenAt == null) state.firstSeenAt = Date.now();
    preloadNextSlide(state);                // ensure 2nd slide is ready
    startRotation(state);
  }

  function onLeaveViewport(state) {
    stopRotation(state);
  }

  function preloadNextSlide(state) {
    const slides = state.carousel.children;
    if (slides.length < 2) return;
    const img = slides[1].querySelector('img');
    if (!img) return;
    if (img.loading === 'lazy') img.loading = 'eager';
    // Touch currentSrc to nudge the browser onto fetching the right source
    // from the srcset; if it's already decoded this is free.
    /* eslint-disable no-unused-expressions */
    img.currentSrc;
  }

  function startRotation(state) {
    if (state.userOwned)  return;            // customer took control
    if (state.timer)      return;            // already running
    if (reduceMotion)     return;            // disabled by user pref
    if (saveData)         return;            // disabled on data-saver
    if (document.hidden)  return;            // tab in background
    if (capReached(state)) return;           // already past 30 s budget

    state.timer = setInterval(() => tick(state), ROTATE_MS);
  }

  function stopRotation(state) {
    if (state.timer) {
      clearInterval(state.timer);
      state.timer = null;
    }
  }

  function capReached(state) {
    if (state.firstSeenAt == null) return false;
    return (Date.now() - state.firstSeenAt) > ROTATE_CAP_MS;
  }

  function tick(state) {
    if (state.paused)     return;            // hover/touch/focus pause
    if (state.userOwned)  return;            // hard stop after user click
    if (capReached(state)) {                 // budget exhausted — stop
      stopRotation(state);
      return;
    }
    advance(state);
  }

  // Scroll the next slide into view, wrapping to slide 0 at the end.
  // Uses smooth scroll so the transition matches a customer swipe.
  function advance(state) {
    const slides = state.carousel.children;
    if (slides.length < 2) return;
    const nextIdx = (state.index + 1) % slides.length;
    const nextSlide = slides[nextIdx];
    if (!nextSlide) return;
    const target = nextSlide.offsetLeft;     // relative to the scroll container
    state.programmaticScroll = true;
    state.carousel.scrollTo({ left: target, behavior: 'smooth' });
    state.index = nextIdx;
    // Allow the browser a beat to settle before re-enabling user-detection.
    clearTimeout(state.settleTimer);
    state.settleTimer = setTimeout(() => {
      state.programmaticScroll = false;
    }, 700);
  }

  function pause(state)  { state.paused = true; }
  function resume(state) { state.paused = false; }

  // Once the customer clicks a thumbnail or drags the carousel, stop
  // auto-rotation for the rest of the page lifetime. The script should
  // never fight the user.
  function takeUserControl(state) {
    state.userOwned = true;
    stopRotation(state);
  }

  function attach(carousel) {
    if (galleries.has(carousel)) return;

    // page-dots is identified via aria-controls — that's the thumb strip
    // the script needs to monitor for clicks (so "user click = stop").
    const galleryId = carousel.id || '';
    const thumbList = galleryId
      ? document.querySelector('page-dots[aria-controls="' + cssEscape(galleryId) + '"]')
      : null;

    const state = {
      carousel,
      thumbList,
      index: 0,
      paused: false,
      userOwned: false,
      timer: null,
      settleTimer: null,
      firstSeenAt: null,
      programmaticScroll: false,
    };
    galleries.set(carousel, state);

    // Pause/resume on hover, touch, focus.
    carousel.addEventListener('mouseenter', () => pause(state));
    carousel.addEventListener('mouseleave', () => resume(state));
    carousel.addEventListener('touchstart',  () => pause(state),  { passive: true });
    carousel.addEventListener('touchend',    () => resume(state), { passive: true });
    carousel.addEventListener('focusin',     () => pause(state));
    carousel.addEventListener('focusout',    () => resume(state));

    // Manual drag → take user control. Distinguishing customer scrolls from
    // our own scrollTo() is the trick: we set programmaticScroll briefly
    // before calling scrollTo and check it on the scroll event.
    carousel.addEventListener('scroll', () => {
      if (state.programmaticScroll) return;
      takeUserControl(state);
    }, { passive: true });

    // Thumb click is also a "stop forever" signal.
    if (thumbList) {
      thumbList.addEventListener('click', () => takeUserControl(state));
    }

    ensureObserver().observe(carousel);
  }

  function detach(carousel) {
    const state = galleries.get(carousel);
    if (!state) return;
    stopRotation(state);
    clearTimeout(state.settleTimer);
    if (observer) observer.unobserve(carousel);
    galleries.delete(carousel);
  }

  // ── Tab visibility ──────────────────────────────────────────────────
  document.addEventListener('visibilitychange', () => {
    galleries.forEach(state => {
      if (document.hidden) stopRotation(state);
      else                 startRotation(state);
    });
  });

  // ── Boot + react to dynamic section re-renders ──────────────────────
  function scan(root) {
    (root || document).querySelectorAll(SLOT).forEach(attach);
  }

  function init() {
    scan(document);

    // Focal re-renders product blocks on variant change. A MutationObserver
    // catches the new <media-carousel data-extras-rotate> nodes and attaches.
    const mo = new MutationObserver(records => {
      for (const r of records) {
        r.addedNodes.forEach(n => {
          if (n.nodeType !== 1) return;
          if (n.matches && n.matches(SLOT)) attach(n);
          else if (n.querySelectorAll)      scan(n);
        });
        r.removedNodes.forEach(n => {
          if (n.nodeType !== 1) return;
          if (n.matches && n.matches(SLOT)) detach(n);
          else if (n.querySelectorAll) {
            n.querySelectorAll(SLOT).forEach(detach);
          }
        });
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  function cssEscape(s) {
    return (window.CSS && CSS.escape)
      ? CSS.escape(s)
      : String(s).replace(/[^a-zA-Z0-9_-]/g, c => '\\' + c);
  }

  // ── Single-image zoom (legacy size-card buttons) ────────────────────
  //
  // The hook-board configurator emits <button data-xgal-zoom data-xgal-src
  // data-xgal-alt> on each size-card chip (4-hook / 5-hook / 7-hook). Those
  // are NOT section-gallery zoom triggers — they open a single specific
  // image (the size diagram) in a modal. We keep a tiny click delegate so
  // those buttons keep working after the section-gallery zoom moved to
  // Focal's product-zoom-button.
  //
  // The modal element is shared with configurator.js (slab build-style
  // zoom) — queried by [data-cfg-modal] so we don't duplicate DOM.

  function ensureLegacyModal() {
    let modal = document.querySelector('[data-cfg-modal]');
    if (modal) return modal;
    modal = document.createElement('div');
    modal.className = 'cfg-modal';
    modal.setAttribute('data-cfg-modal', '');
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Image preview');
    modal.hidden = true;
    modal.innerHTML =
      '<div class="cfg-modal__backdrop" data-cfg-modal-close></div>' +
      '<div class="cfg-modal__dialog">' +
        '<button type="button" class="cfg-modal__close" data-cfg-modal-close aria-label="Close">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true">' +
            '<line x1="6" y1="6" x2="18" y2="18"></line>' +
            '<line x1="18" y1="6" x2="6" y2="18"></line>' +
          '</svg>' +
        '</button>' +
        '<img class="cfg-modal__img" alt="">' +
      '</div>';
    document.body.appendChild(modal);
    modal.addEventListener('click', e => {
      if (e.target.closest('[data-cfg-modal-close]')) closeLegacyModal(modal);
    });
    return modal;
  }

  function openLegacyModal(src, alt) {
    if (!src) return;
    const m = ensureLegacyModal();
    const img = m.querySelector('.cfg-modal__img');
    img.src = src;
    img.alt = alt || '';
    m.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    m.__legacyKey = e => { if (e.key === 'Escape') closeLegacyModal(m); };
    document.addEventListener('keydown', m.__legacyKey);
    m.querySelector('.cfg-modal__close').focus();
  }

  function closeLegacyModal(m) {
    if (!m || m.hidden) return;
    m.hidden = true;
    const img = m.querySelector('.cfg-modal__img');
    if (img) img.removeAttribute('src');
    document.documentElement.style.overflow = '';
    if (m.__legacyKey) {
      document.removeEventListener('keydown', m.__legacyKey);
      m.__legacyKey = null;
    }
  }

  // Only buttons with an EXPLICIT data-xgal-src open here. Buttons without
  // that attribute were the old section-gallery zoom triggers and are no
  // longer in the markup — Focal's product-zoom-button replaces them.
  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-xgal-zoom][data-xgal-src]');
    if (!btn) return;
    e.preventDefault();
    openLegacyModal(btn.dataset.xgalSrc, btn.dataset.xgalAlt || '');
  });
})();
